import { useLayoutEffect, useState } from "react";

/** The content card's stable anchor id, emitted by the library's AppShell. */
export const CONTENT_ANCHOR_ID = "__dialog-anchor";

/** The region BELOW a screen's view tabs. A route stamps it on the container
 *  wrapping its view content; the panels that belong to the view — filters,
 *  display options — pin inside it so the tabs stay visible above them. */
export const VIEW_PANE_ANCHOR_ID = "__view-pane-anchor";

export type AnchorInsets = {
  top: number;
  right: number;
  bottom: number;
  /** The anchor has scrolled more than half out of its scroller, so a panel
   *  pinned to it would sit mostly over rows that are not its own. A host
   *  that pins over a scrolling anchor closes on this (Spencer, 2026-09-15:
   *  "if its half out of view we close it"); a pane anchor never sets it. */
  hidden: boolean;
};

const ZERO_INSETS: AnchorInsets = { bottom: 0, right: 0, top: 0, hidden: false };

/**
 * Pins an overlay INSIDE a page region: the viewport-relative insets of the
 * element carrying `anchorId`, applied as margins on a `Sheet` popup (its
 * viewport is `fixed inset-0`) so the panel opens within the region — below
 * the view tabs, inside the content card's border — instead of spanning the
 * viewport. Left stays free: the popup's own width sets it. Re-measured on
 * window resize, on anchor resizes (sidebar drags shift the region without a
 * window resize) and on any scroll (a card anchor rides its board).
 *
 * A host with no region of its own passes {@link CONTENT_ANCHOR_ID}; a panel
 * that should prefer the tab region when present uses
 * {@link useViewPaneAnchorInsets}.
 */
export function useAnchorInsets(
  anchorId: string | undefined,
  open: boolean,
): AnchorInsets | null {
  const [insets, setInsets] = useState<AnchorInsets | null>(null);
  // Layout effect: the popup mounts in the same commit `open` flips true, and
  // an after-paint measure would draw its first frame unpinned at viewport
  // height. It is also what lets the fullscreen↔sidebar FLIP measure the
  // popup's true final rect. On close only the LISTENERS drop — the last
  // insets survive so the popup holds its pinned bounds through the exit
  // animation instead of snapping tall for its closing frames.
  useLayoutEffect(() => {
    if (!anchorId) {
      setInsets(null);
      return;
    }
    if (!open) return;
    const el = document.getElementById(anchorId);
    if (!el) {
      setInsets(null);
      return;
    }
    // The nearest ancestor that scrolls the anchor. An anchor scrolled partly
    // out of it (a card under its board's top edge) is only visible inside
    // it, so the panel clips to that box rather than painting over whatever
    // sits above the scroller — the page's own header bar.
    const scroller = (() => {
      for (let node = el.parentElement; node; node = node.parentElement) {
        const { overflowY } = window.getComputedStyle(node);
        if (
          (overflowY === "auto" || overflowY === "scroll") &&
          node.scrollHeight > node.clientHeight
        ) {
          return node;
        }
      }
      return null;
    })();
    const measure = () => {
      // Inner (padding-box) edges: the rect includes the anchor's border, and
      // an overlay pinned to the outer rect paints OVER that border and its
      // rounded corner instead of sitting inside the region.
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      let top = rect.top + parseFloat(style.borderTopWidth);
      let bottom = rect.bottom - parseFloat(style.borderBottomWidth);
      let hidden = false;
      if (scroller) {
        const box = scroller.getBoundingClientRect();
        top = Math.max(top, box.top);
        bottom = Math.min(bottom, box.bottom);
        hidden = bottom - top < rect.height / 2;
      }
      setInsets({
        bottom: Math.max(0, window.innerHeight - Math.max(bottom, top)),
        right: Math.max(
          0,
          window.innerWidth - rect.right + parseFloat(style.borderRightWidth),
        ),
        top: Math.max(0, top),
        hidden,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener("resize", measure);
    // The anchor can MOVE without resizing: a widget card scrolls with its
    // board while the panel, a fixed layer, would stay put over the rows
    // that scrolled in under it. Any scroll in the document (capture, since
    // scroll never bubbles) re-measures on the next frame, so the panel
    // rides its anchor. A pane anchor never moves on scroll, so for it the
    // listener only reads an unchanged rect.
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      document.removeEventListener("scroll", onScroll, { capture: true });
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [anchorId, open]);
  return insets;
}

/**
 * {@link useAnchorInsets} bound to the region below a screen's view tabs
 * ({@link VIEW_PANE_ANCHOR_ID}), falling back to the shell's content card when
 * the host stamps no tab region. A panel that belongs to the view — the
 * manage-widgets panel on a tab-connected home like Team Overview — pins
 * under the tabs so they stay visible and clickable above it; on a tabless
 * home like My Work the same panel takes the content-card presentation: a
 * right-side sheet inset below the site header and inside the shell gutter,
 * THE side-panel presentation, so the UX never forks. Resolves to zero insets
 * — a full-height sheet — when both anchors are absent, e.g. a route rendered
 * outside the shell.
 */
export function useViewPaneAnchorInsets(open: boolean): AnchorInsets {
  const viewPane = useAnchorInsets(VIEW_PANE_ANCHOR_ID, open);
  const content = useAnchorInsets(CONTENT_ANCHOR_ID, open);
  return viewPane ?? content ?? ZERO_INSETS;
}
